// ===================================================
// src/components/Planning/ProjectedInventoryChart.js
// 📈 Projected Inventory Chart – Final Stable Version
// ===================================================
import React, { useState, useRef, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

const COLORS = {
  total_cityplay: "#0ea5e9",
  total_20: "#10b981",
  us_cityplay_m: "#1d4ed8",
  us_cityplay_l: "#3b82f6",
  us_20_m: "#15803d",
  us_20_l: "#22c55e",
  amazon_us_cityplay_m: "#60a5fa",
  amazon_us_cityplay_l: "#93c5fd",
  amazon_us_20_m: "#86efac",
  amazon_us_20_l: "#bbf7d0",
  uk_cityplay_m: "#9333ea",
  uk_cityplay_l: "#a855f7",
  uk_20_m: "#c2410c",
  uk_20_l: "#ea580c",
  amazon_uk_cityplay_m: "#d8b4fe",
  amazon_uk_cityplay_l: "#e9d5ff",
  amazon_uk_20_m: "#fdba74",
  amazon_uk_20_l: "#fed7aa",
};

const SKU_KEYS = Object.keys(COLORS).filter((k) => !k.startsWith("total_"));

const ProjectedInventoryChart = ({ data }) => {
  const [zoom, setZoom] = useState(1);
  const [yZoom, setYZoom] = useState(1);
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const chartRef = useRef();

  const chartData =
    Array.isArray(data) && data.length > 0
      ? data.map((day) => {
          const s = day.stock || {};
          const a = s.amazon || {};
          const total_cityplay =
            (s.us_cityplay_m || 0) +
            (s.us_cityplay_l || 0) +
            (s.uk_cityplay_m || 0) +
            (s.uk_cityplay_l || 0) +
            (a.us_cityplay_m || 0) +
            (a.us_cityplay_l || 0) +
            (a.uk_cityplay_m || 0) +
            (a.uk_cityplay_l || 0);
          const total_20 =
            (s.us_20_m || 0) +
            (s.us_20_l || 0) +
            (s.uk_20_m || 0) +
            (s.uk_20_l || 0) +
            (a.us_20_m || 0) +
            (a.us_20_l || 0) +
            (a.uk_20_m || 0) +
            (a.uk_20_l || 0);
          return {
            date: day.date,
            total_cityplay,
            total_20,
            ...Object.fromEntries(
              SKU_KEYS.map((key) => {
                const cleanKey = key.replace("amazon_", "");
                const value = key.startsWith("amazon_")
                  ? a[cleanKey] || 0
                  : s[cleanKey] || 0;


                return [key, value];
              })
            )
            ,
          };
        })
      : [];

  const totalPoints = chartData.length;
  const todayISO = new Date().toISOString().split("T")[0];
  const visibleCount = Math.max(Math.floor(totalPoints / zoom), 5);

  useEffect(() => {
  if (chartRef.current) chartRef.current.style.cursor = "grab";
}, []);

  const safeOffset = Math.min(
    Math.max(0, Math.floor(offset)),
    Math.max(0, totalPoints - visibleCount)
  );

  const visibleData =
    totalPoints > 0
      ? chartData.slice(
          safeOffset,
          Math.min(totalPoints, safeOffset + visibleCount)
        )
      : [];

  useEffect(() => {
    if (safeOffset !== offset) setOffset(safeOffset);
  }, [offset, safeOffset]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev * 1.8, 40));
    setYZoom((prev) => Math.min(prev * 1.2, 10));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev / 1.8, 1));
    setYZoom((prev) => Math.max(prev / 1.2, 1));
  };

  const handleReset = () => {
    setZoom(1);
    setYZoom(1);
    setOffset(0);
  };

const handleMouseDown = (e) => {
  if (e?.preventDefault) e.preventDefault();
  setIsDragging(true);
  setStartX(e.clientX || e?.activeCoordinate?.x || 0);
  if (chartRef.current) chartRef.current.style.cursor = "grabbing";
};

const handleMouseMove = (e) => {
  if (!isDragging) return;
  const clientX = e.clientX || e?.activeCoordinate?.x;
  if (!clientX) return;

  const delta = clientX - startX;
  const ratio = totalPoints / chartRef.current?.offsetWidth || 1;

  setOffset((prev) =>
    Math.min(Math.max(prev - delta * ratio, 0), totalPoints - visibleCount)
  );
  setStartX(clientX);
};


const stopDragging = () => {
  setIsDragging(false);
  if (chartRef.current) chartRef.current.style.cursor = "grab";
};


  return (
    <div
      style={{
        background: "white",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        padding: "1rem",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        userSelect: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>📊 Projected Inventory</h3>
        <div>
          <button onClick={handleZoomIn}>➕ Zoom In</button>{" "}
          <button onClick={handleZoomOut}>➖ Zoom Out</button>{" "}
          <button onClick={handleReset}>🔁 Reset</button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={600}>
        <LineChart
          data={visibleData}
          ref={chartRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={stopDragging}
          onMouseLeave={stopDragging}
          margin={{ top: 20, right: 30, left: 10, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11 }}
            tickFormatter={(d) =>
              new Date(d).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
              })
            }
          />
          <YAxis
            tick={{ fontSize: 11 }}
            domain={[
              (dataMin) => dataMin / yZoom,
              (dataMax) => dataMax * yZoom,
            ]}
          />
          <Tooltip />
          <Legend />
          <ReferenceLine
            x={todayISO}
            stroke="red"
            strokeDasharray="4 4"
            label={{ value: "📍 Today", position: "top", fill: "red" }}
          />
          <Line
            type="monotone"
            dataKey="total_cityplay"
            stroke={COLORS.total_cityplay}
            strokeWidth={3}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="total_20"
            stroke={COLORS.total_20}
            strokeWidth={3}
            dot={false}
          />
          {SKU_KEYS.map((key) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[key]}
              strokeWidth={1.2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ProjectedInventoryChart;
